import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { ApolloProvider } from '@apollo/client';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import client from './apollo-client';
import EnhancedLogin from './pages/EnhancedLogin';
import Dashboard from './pages/Dashboard';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import { Toaster } from 'react-hot-toast';
import InstallPrompt from './components/InstallPrompt';
import LocationTrackingPrompt from './components/location/LocationTrackingPrompt';
import versionChecker from './utils/versionCheck';
import CacheBustDetector from './components/CacheBustDetector';
import UpdateNotification from './components/UpdateNotification';
import { logger } from './utils/logger';
import './App.css';
import { SharedJobDetails } from './pages/SharedJobDetails';
import { SharedReportDetails } from './pages/SharedReportDetails';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';



function AppRoutes() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const redirectParam = new URLSearchParams(location.search).get('redirect');
  const safeRedirect = redirectParam?.startsWith('/dashboard') ? redirectParam : '/dashboard';
  
  // Add state to track profile completion status
  const [profileStatus, setProfileStatus] = useState({
    checked: false,
    isComplete: false,
    isChecking: false
  });

  // Effect to handle first login and redirect to settings page
  useEffect(() => {
    // Only run if we have a user and loading is complete
    if (!loading && user) {
      
      // For first-time users, redirect to user-settings if not already there
      if (user.isFirstLogin && 
          !location.pathname.includes('/dashboard/user-settings')) {
        
        // Mark as checked to prevent infinite redirects
        setProfileStatus({
          checked: true,
          isComplete: false,
          isChecking: false
        });
        
        // Redirect to settings page with first login message
        navigate('/dashboard/user-settings', { 
          state: { 
            isFirstLogin: true,
            message: 'Please complete your profile to continue' 
          },
          replace: true
        });
        
      } else if (!user.isFirstLogin && 
                 !profileStatus.checked && 
                 !location.pathname.includes('/dashboard')) {
        
        // Mark as checked to prevent infinite redirects
        setProfileStatus({
          checked: true,
          isComplete: true,
          isChecking: false
        });
        
        // Regular users can stay on current path or go to dashboard
        if (location.pathname === '/login' || location.pathname === '/') {
          navigate('/dashboard', { replace: true });
        }
      }
    }
  }, [user, loading, location.pathname, profileStatus.checked, navigate]);


  // Show loading indicator while authentication is being checked - but only on protected routes
  if (loading && !location.pathname.includes('/login') && 
      !location.pathname.includes('/forgot-password') && 
      !location.pathname.includes('/reset-password')) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-600"></div>
      </div>
    );
  }

  return (
    <Routes>
      <Route 
        path="/login" 
        element={user && profileStatus.checked ? <Navigate to={user.isFirstLogin ? "/dashboard/user-settings" : safeRedirect} replace /> : <EnhancedLogin />}
      />
      <Route 
        path="/dashboard/*" 
        element={
          user ? (
            <Dashboard />
          ) : (
            <Navigate
              to={`/login?redirect=${encodeURIComponent(`${location.pathname}${location.search}`)}`}
              replace
            />
          )
        } 
      />
      <Route 
        path="/forgot-password" 
        element={<ForgotPassword />} 
      />
      <Route 
        path="/reset-password" 
        element={<ResetPassword />} 
      />
      <Route 
        path="/" 
        element={<Navigate to={user ? "/dashboard" : "/login"} replace />} 
      />
      <Route 
        path="/shared/jobs/:jobId" 
        element={<SharedJobDetails />} 
      />
      <Route 
        path="/shared/reports/:reportId" 
        element={<SharedReportDetails />} 
      />
      <Route 
        path="*" 
        element={<Navigate to={user ? "/dashboard" : "/login"} replace />} 
      />
    </Routes>
  );
}

function App() {
  // Check if cookies are enabled on initial load
  React.useEffect(() => {
    const checkCookiesEnabled = () => {
      try {
        // Try to set a test cookie
        document.cookie = 'testcookie=1; path=/';
        const cookieEnabled = document.cookie.indexOf('testcookie') !== -1;
        
        // Clear test cookie
        document.cookie = 'testcookie=; expires=Thu, 01 Jan 1970 00:00:01 GMT; path=/';
        
        if (!cookieEnabled) {
          alert('Warning: Cookies are disabled in your browser. This application requires cookies for authentication. Please enable cookies to continue.');
        }
      } catch (e) {
        // Silent fail for cookie check errors
      }
    };
    
    checkCookiesEnabled();
  }, []);

  // Initialize version checker for manual updates
  React.useEffect(() => {
    const initVersionChecker = () => {
      try {
        versionChecker.startVersionCheck();
        
        // Listen for service worker messages
        if ('serviceWorker' in navigator) {
          navigator.serviceWorker.addEventListener('message', (event) => {
            if (event.data && event.data.type === 'FORCE_RELOAD') {
              logger.debug('Service worker requested force reload', { reason: event.data.reason });
              window.location.reload(true);
            }
          });
        }
        
        // Cleanup function to stop version checking when app unmounts
        return () => {
          versionChecker.stopVersionCheck();
        };
      } catch (e) {
        // Silent fail for version checker initialization
      }
    };

    const cleanup = initVersionChecker();
    return cleanup;
  }, []);

  // Service worker message handling removed to prevent infinite reload loops

  // Add error boundary for critical app errors only
  const [appError, setAppError] = React.useState(null);

  // If there's a fatal app error, show it
  if (appError) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-red-50 p-4">
        <h1 className="text-2xl font-bold text-red-800 mb-4">Application Error</h1>
        <p className="text-red-700 mb-4">{appError.message || "An unknown error occurred"}</p>
        <button 
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          onClick={() => window.location.reload()}
        >
          Reload Application
        </button>
      </div>
    );
  }

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <ApolloProvider client={client}>
        <Router>
          <AuthProvider onError={(err) => {
            if (err && err.message && (
              err.message.includes('Failed to fetch') ||
              err.message.includes('Network error') ||
              err.message.includes('Connection failed')
            )) {
              setAppError(err);
            }
          }}>
            <AppRoutes />
            <InstallPrompt />
            <LocationTrackingPrompt />
            <CacheBustDetector />
            <UpdateNotification />
            <Toaster position="top-right" />
          </AuthProvider>
        </Router>
      </ApolloProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
